import { inject } from '@angular/core';
import { RedirectFunction, Router } from '@angular/router';

/**
 * Where an embedded Stripe session comes back to. Twinny X never sends the customer to Stripe:
 * the checkout and the card change are Stripe's embedded forms inside the app
 * (`twx-stripe-checkout`, `is_hosted=false`). Once done, Stripe takes the tab to the session's
 * return URL, which twinny-api writes itself (`stripe.return-page`: `main/settings/account`,
 * Twinny's path, with `?session_id=`). The screen that opens Stripe says here where it wants
 * to be back (`{SESSION_ID}` takes the session's id), and that path sends it there.
 */
const KEY = 'twx:stripe_return';

/** Where twinny-api sends an embedded session back to, under the app's URL. */
export const TWX_STRIPE_RETURN_PATH = 'main/settings/account';

/** Without a screen that asked (another tab, storage off): the plan, which reads the subscription again. */
const FALLBACK = '/settings/billing?checkout=done';

export const rememberStripeReturn = (to: string) => {
    try {
        sessionStorage.setItem(KEY, to);
    } catch {
        /* storage unavailable: the plan */
    }
};

/** The sign-up's progress (twx-onboarding): paying its plan, Stripe comes back to it. */
const SIGNUP_KEY = 'twx:onboarding';
const SIGNUP_RETURN = '/auth/start?checkout=done';

/** Without the screen's own return: the sign-up when it was paying its plan, else the plan. */
const fallback = () => {
    try {
        const signup = JSON.parse(sessionStorage.getItem(SIGNUP_KEY) ?? 'null') as { step?: string; planDone?: boolean } | null;
        if (signup?.step === 'plan' && !signup.planDone) return SIGNUP_RETURN;
    } catch {
        /* storage unavailable or unreadable: the plan */
    }
    return FALLBACK;
};

export const twxStripeReturn: RedirectFunction = ({ queryParams }) => {
    let to = FALLBACK;
    try {
        to = sessionStorage.getItem(KEY) || fallback();
        sessionStorage.removeItem(KEY);
    } catch {
        /* storage unavailable: the plan */
    }
    const session = typeof queryParams['session_id'] === 'string' ? queryParams['session_id'] : '';
    // A screen that needs the session (the card change) takes its id; without one, its param goes
    const url = session
        ? to.replace('{SESSION_ID}', encodeURIComponent(session))
        : to.replace(/[?&][a-z_]+=\{SESSION_ID\}/i, '');
    return inject(Router).parseUrl(url.startsWith('/') ? url : `/${url}`);
};
